import { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, CaretRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT = {
  build: { label: "BUILD", cls: "bg-emerald-600 text-white" },
  wait: { label: "WAIT", cls: "bg-amber-500 text-white" },
  pass: { label: "PASS", cls: "bg-rose-600 text-white" },
  insufficient: { label: "INSUFFICIENT", cls: "bg-zinc-400 text-white" },
};

const scoreCls = (s) => (s >= 70 ? "text-emerald-600" : s >= 50 ? "text-amber-600" : "text-rose-600");

const weakest = (rows) => {
  const scored = (rows || []).filter((r) => r.score != null);
  if (!scored.length) return null;
  return scored.reduce((a, b) => (b.score < a.score ? b : a));
};

const DecisionBoard = ({ onOpen }) => {
  const [reports, setReports] = useState(null);
  const [batch, setBatch] = useState(null);

  const load = () => axios.get(`${API}/commerce/decision-reports`)
    .then(({ data }) => setReports(data.reports || []))
    .catch(() => setReports([]));
  useEffect(() => { load(); }, []);

  const pollBatch = () => axios.get(`${API}/commerce/decision-report-batch/status`)
    .then(({ data }) => setBatch(data)).catch(() => {});
  useEffect(() => {
    if (batch?.state !== "RUNNING" && batch?.state !== "STARTING") return;
    const iv = setInterval(() => { pollBatch(); load(); }, 6000);
    return () => clearInterval(iv);
  }, [batch?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const runBatch = () => {
    axios.post(`${API}/commerce/decision-report-batch/run`, { regenerate: false })
      .then(() => setBatch({ state: "STARTING" })).then(pollBatch).catch(() => {});
  };

  const batchRunning = batch?.state === "RUNNING" || batch?.state === "STARTING";

  if (!reports) return <div className="text-center py-12 text-zinc-400" data-testid="decision-board-loading">Loading decision reports…</div>;

  if (!reports.length) {
    return (
      <div className="rounded-2xl border border-dashed border-zinc-300 bg-white/60 p-10 text-center" data-testid="decision-board-empty">
        <Trophy size={28} className="mx-auto text-violet-400 mb-2" />
        <h3 className="font-bold text-zinc-800">No decision reports yet</h3>
        <p className="text-sm text-zinc-500 mt-1 max-w-xl mx-auto">
          Generate one from <b>Due Diligence</b> — pick an opportunity and click "Generate Decision Report" (Phase 12).
          Each report ranks here automatically so you can compare opportunities side by side.
        </p>
        <button onClick={runBatch} disabled={batchRunning} data-testid="decision-batch-generate-empty"
          className="mt-4 text-[11px] font-bold px-4 py-2 rounded-full bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">
          {batchRunning ? "Running…" : "Generate all eligible reports"}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="decision-board">
      <div className="flex items-center justify-between flex-wrap gap-2 pb-1">
        <span className="text-[11px] text-zinc-500">{reports.length} report{reports.length === 1 ? "" : "s"}</span>
        <div className="flex items-center gap-2">
          {batchRunning && (
            <span className="text-[11px] font-medium text-amber-700 bg-amber-50 ring-1 ring-amber-200 px-3 py-1 rounded-full" data-testid="decision-batch-progress">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse mr-1.5" />
              Generating… {batch?.done ?? 0}/{batch?.total ?? "?"}{batch?.current ? ` · ${batch.current}` : ""}
            </span>
          )}
          {batch?.state === "FINISHED" && <span className="text-[11px] text-emerald-600 font-semibold" data-testid="decision-batch-done">✓ Batch complete</span>}
          <button onClick={runBatch} disabled={batchRunning} data-testid="decision-batch-generate"
            className="text-[11px] font-bold px-4 py-1.5 rounded-full bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">
            {batchRunning ? "Running…" : "Generate all eligible reports"}
          </button>
        </div>
      </div>
      <div className="hidden sm:grid grid-cols-[44px_1fr_110px_90px_80px_1fr_90px] gap-3 px-4 text-[10px] font-mono uppercase tracking-wider text-zinc-400">
        <span>Rank</span><span>Opportunity</span><span>Market</span><span>Verdict</span><span>Score</span><span>Weakest dimension</span><span></span>
      </div>
      {reports.map((r, i) => {
        const v = VERDICT[r.verdict] || VERDICT.insufficient;
        const w = weakest(r.scorecard);
        return (
          <button key={r.cluster_id} onClick={() => onOpen?.(r.cluster_id)}
            className="w-full grid sm:grid-cols-[44px_1fr_110px_90px_80px_1fr_90px] grid-cols-2 gap-3 items-center text-left bg-white border border-zinc-200 rounded-xl px-4 py-3 hover:border-violet-300 hover:shadow-md transition-all"
            data-testid={`decision-row-${r.cluster_id}`}>
            <span className="text-lg font-black text-zinc-300">#{i + 1}</span>
            <span>
              <span className="block text-sm font-semibold text-zinc-900 leading-snug">{r.label || r.cluster_id}</span>
              <span className="text-[10px] font-mono text-zinc-400">{r.cluster_id} · {r.evidence_completeness?.present}/{r.evidence_completeness?.total} phases</span>
            </span>
            <span className="text-[11px] font-medium text-zinc-500">{r.market}</span>
            <span><span className={`text-[10px] font-black px-2.5 py-1 rounded-full ${v.cls}`} data-testid={`decision-verdict-${r.cluster_id}`}>{v.label}</span></span>
            <span className={`text-2xl font-black ${r.overall_score != null ? scoreCls(r.overall_score) : "text-zinc-300"}`} data-testid={`decision-score-${r.cluster_id}`}>
              {r.overall_score != null ? r.overall_score : "—"}
            </span>
            <span className="text-[11px] text-zinc-500">
              {w ? <>{w.dimension} <b className={scoreCls(w.score)}>{w.score}</b> <span className="text-[9px] text-zinc-400">({w.source_phase})</span></> : "—"}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-600 justify-self-end">
              Open <CaretRight size={12} />
            </span>
          </button>
        );
      })}
      <p className="text-[10px] text-zinc-400 pt-1">Ranked by overall score · scores come straight from each locked phase · click a row for the full report.</p>
    </div>
  );
};

export default DecisionBoard;
